/**
 * Shared constants for the /help/contact client bundle.
 *
 * WHAT THIS FILE DOES:
 * - Centralizes numeric limits, allowed MIME types, and UI strings.
 * - Mirrors backend validation rules so the client can fail fast.
 * - Keeps magic numbers out of UI/controller logic.
 *
 * WHY THIS EXISTS:
 * - Support message validation spans text, audio, and attachments.
 * - Multiple helpers (form controller, attachment validation, voice recorder)
 *   need the same constraints and labels.
 * - Centralizing prevents drift between client and backend rules.
 *
 * ARCHITECTURE CONTEXT:
 * - Used by support-form-controller.ts and attachments helpers.
 * - Must stay aligned with support-messages constants in the backend.
 * - Inspired by extension support-message-modal.js constants.
 *
 * RELATED FILES:
 * - packages/backend/src/api/routes/support-messages/constants.ts
 * - packages/chrome-plugin/extension/ui/options/support-message-modal.js
 * - packages/backend/public/static/help-contact.css
 *
 * NON-GOALS:
 * - No runtime logic in this module (constants only).
 * - No API interaction (handled by support-message-api.ts).
 *
 * FILE HEADER SIZE TARGET:
 * - Keep this header between 50-120 lines for LLM readability.
 */

export const TimeConstants = {
  /** Milliseconds per second. */
  MS_PER_SECOND: 1000,
  /** Seconds per minute. */
  SECONDS_PER_MINUTE: 60,
  /** Timer update cadence for recording/playback. */
  TIMER_TICK_MS: 1000,
  /** Left-pad length for seconds display. */
  TIME_PAD_LENGTH: 2
} as const;

export const ByteConstants = {
  /** Bytes per kilobyte. */
  KB: 1024,
  /** Bytes per megabyte. */
  MB: 1024 * 1024,
  /** Bytes per gigabyte. */
  GB: 1024 * 1024 * 1024
} as const;

export const Limits = {
  /** Max text length for support messages. */
  MAX_TEXT_CHARS: 50000,
  /** Max voice note duration (10 minutes). */
  MAX_RECORDING_MS: 10 * TimeConstants.SECONDS_PER_MINUTE * TimeConstants.MS_PER_SECOND,
  /** Max audio file size (25 MB). */
  MAX_AUDIO_BYTES: 25 * ByteConstants.MB,
  /** Max attachment size per file (2 GB). */
  MAX_ATTACHMENT_BYTES: 2 * ByteConstants.GB
} as const;

export const RecordingFormats = [
  { mimeType: 'audio/webm;codecs=opus', extension: 'webm' },
  { mimeType: 'audio/webm', extension: 'webm' },
  { mimeType: 'audio/mp4', extension: 'mp4' }
] as const;

export const AttachmentDirectMimeTypes = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf'
] as const;

export const AttachmentDirectMimeTypesSet = new Set<string>(AttachmentDirectMimeTypes);

export const AttachmentDirectExtensions = [
  'jpeg',
  'jpg',
  'pdf',
  'png',
  'webp'
] as const;

export const AttachmentDirectExtensionsSet = new Set<string>(AttachmentDirectExtensions);

export const AttachmentTranscodeVideoMimeTypes = [
  'video/3gpp',
  'video/3gpp2',
  'video/mp2t',
  'video/mpeg',
  'video/mp4',
  'video/ogg',
  'video/quicktime',
  'video/webm',
  'video/x-flv',
  'video/x-matroska',
  'video/x-ms-wmv',
  'video/x-msvideo'
] as const;

export const AttachmentTranscodeVideoMimeTypesSet = new Set<string>(AttachmentTranscodeVideoMimeTypes);

export const AttachmentTranscodeVideoExtensions = [
  '3g2',
  '3gp',
  'avi',
  'flv',
  'm2ts',
  'm4v',
  'mkv',
  'mov',
  'mp4',
  'mpe',
  'mpeg',
  'mpg',
  'mts',
  'ogv',
  'ts',
  'webm',
  'wmv'
] as const;

export const AttachmentTranscodeVideoExtensionsSet = new Set<string>(AttachmentTranscodeVideoExtensions);

export const AttachmentAllowedMimeTypes = new Set<string>([
  ...AttachmentDirectMimeTypes,
  ...AttachmentTranscodeVideoMimeTypes
]);

export const AttachmentAllowedExtensions = new Set<string>([
  ...AttachmentDirectExtensions,
  ...AttachmentTranscodeVideoExtensions
]);

export const AttachmentAcceptValue = [
  ...AttachmentDirectMimeTypes,
  ...AttachmentTranscodeVideoMimeTypes,
  ...AttachmentDirectExtensions.map((extension) => `.${extension}`),
  ...AttachmentTranscodeVideoExtensions.map((extension) => `.${extension}`)
].join(',');

export const AttachmentTypeLabels = {
  IMAGE: 'Image',
  PDF: 'PDF',
  VIDEO: 'Video',
  FILE: 'File'
} as const;

export const AttachmentBlockedExtensions = new Set<string>([
  'apk',
  'app',
  'bat',
  'cmd',
  'com',
  'dmg',
  'dll',
  'exe',
  'jar',
  'js',
  'msi',
  'pkg',
  'ps1',
  'scr',
  'sh',
  'vbs'
]);

export const AttachmentErrors = {
  TYPE_UNSUPPORTED: 'Only JPEG, PNG, WebP, PDF, or supported video files are allowed.',
  EXECUTABLE: 'Executable files are not allowed.',
  TOO_LARGE: 'Attachment is too large. Max size is 2 GB.',
  EMPTY: 'Attachment is empty.'
} as const;

export const RecordingErrors = {
  PERMISSION: 'Microphone permission denied. Please enable it in your browser settings.',
  UNSUPPORTED: 'Recording is not supported in this browser.',
  FAILED: 'Recording failed. Please try again.',
  TOO_LARGE: 'Recording is too large. Please keep it under 10 minutes.'
} as const;

export const PlaybackIcons = {
  PLAY: 'play',
  PAUSE: 'pause'
} as const;
